// src/services/providers.js — BYO provider endpoint resolution (fix #2).
//
// Owner decision (2026-09-08, docs/deploy_fixes.md): every live bot resolves
// its model call through the tenant's own connected llm_provider_credential
// (the BYO integration). There is NO platform-hosted model tier for real
// tenants; the platform router is dev/testing-only and hard-blocked in
// production. Provider base URLs are NOT stored on the credential for known
// providers (only `custom` saves an endpoint) — this module maps the known
// provider ids to their OpenAI-compatible base URLs and resolves the effective
// endpoint at call time.

export const PROVIDER_BASE_URL = Object.freeze({
  // OpenAI-compatible chat-completions endpoints.
  // deepseek-v4-flash is served by api.deepseek.com (verified 2026-09-08).
  deepseek: 'https://api.deepseek.com/v1',
  openrouter: 'https://openrouter.ai/api/v1',
  openai: 'https://api.openai.com/v1',
  zai: 'https://api.z.ai/api/paas/v4', // Z.ai coding PaaS OpenAI-compatible base
  google: 'https://generativelanguage.googleapis.com/v1beta/openai', // Gemini OpenAI-compat
  anthropic: 'https://api.anthropic.com/v1' // Anthropic messages API — see resolveProviderBaseUrl note
});

// Providers whose HTTP contract is NOT OpenAI-compatible chat/completions with
// a Bearer key. Calls to these require a `custom`-style endpoint and are
// otherwise rejected with a clear error rather than a silent wrong-format call.
export const NON_OPENAI_COMPAT = Object.freeze(['anthropic']);

/**
 * Resolve the base URL for a credential. Known providers map through
 * PROVIDER_BASE_URL; `custom` uses the credential's own endpoint. Returns null
 * when no endpoint can be resolved (caller must surface the clear error).
 */
export function resolveProviderBaseUrl(credential) {
  if (!credential) return null;
  if (credential.provider === 'custom' || credential.endpoint) {
    return (credential.endpoint || '').trim().replace(/\/+$/, '') || null;
  }
  return PROVIDER_BASE_URL[credential.provider] || null;
}

/**
 * True when the provider can be called directly from the browser with an
 * OpenAI-compatible POST to `{base}/chat/completions` and Bearer auth.
 * Anthropic is OpenAI-incompatible (messages API, x-api-key) — flag it so the
 * UI/chat can say "use a custom OpenAI-compatible endpoint" instead of failing.
 */
export function isDirectOpenAiCompatible(credential) {
  if (!credential) return false;
  if (credential.provider === 'custom') return true;
  return !NON_OPENAI_COMPAT.includes(credential.provider);
}

/**
 * OpenAI-compatible chat completion against a BYO credential.
 * `messages` = [{role, content}...]. Never logs or stores content (provider
 * call is a metered pass-through; client-local like the credential itself).
 */
export async function callOpenAiCompatible({ credential, messages, maxTokens, signal }) {
  const base = resolveProviderBaseUrl(credential);
  if (!base) {
    const error = new Error(`No endpoint configured for provider "${credential?.provider}". Re-save the connection with an endpoint.`);
    error.code = 'PROVIDER_ENDPOINT_MISSING';
    throw error;
  }
  const model = credential.model;
  if (!model) {
    const error = new Error(`No model id set for "${credential?.label}". Edit the connection and set a model.`);
    error.code = 'PROVIDER_MODEL_MISSING';
    throw error;
  }

  const controller = signal instanceof AbortSignal
    ? signal
    : new AbortController().signal;

  const response = await fetch(`${base}/chat/completions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${credential.apiKey}`
    },
    body: JSON.stringify({
      model,
      messages,
      ...(maxTokens ? { max_tokens: maxTokens } : {})
    }),
    signal: controller
  });

  if (!response.ok) {
    let detail = '';
    try {
      const body = await response.text();
      detail = (body || '').slice(0, 300);
    } catch { /* ignore body read failure */ }
    const error = new Error(
      `Provider returned HTTP ${response.status}${detail ? `: ${detail}` : ''}`
    );
    error.status = response.status;
    error.code = response.status === 429
      ? 'MODEL_OVERLOADED'
      : response.status >= 500
        ? 'MODEL_UNAVAILABLE'
        : 'MODEL_REQUEST_REJECTED';
    throw error;
  }

  const data = await response.json().catch(() => {
    throw new Error('Provider returned invalid JSON.');
  });
  const text = data?.choices?.[0]?.message?.content;
  if (typeof text !== 'string') {
    throw new Error('Provider response missing choices[0].message.content.');
  }
  return { text, model, usage: data?.usage ?? null };
}
